/**
 * Truck Loading — OPTIONAL verification step before departure.
 *
 * Pick a run → the full bag manifest is preloaded once → every barcode scan
 * is verified LOCALLY (instant green/red), no server round-trip per scan and
 * no state change on the server (pure anti-mistake check, skippable).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Boxes, CheckCircle, XCircle, RotateCcw, ScanLine, User, Car, Calendar } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PdaLoading({ onLogout }) {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // route summary row
  const [manifest, setManifest] = useState(null);   // { route, bags, total_bags }
  const [loaded, setLoaded] = useState(new Set());  // barcodes verified onto the truck
  const [lastScan, setLastScan] = useState(null);   // { status: 'ok'|'dup'|'wrong', bag?, code }
  const [flash, setFlash] = useState(null);         // { status } — full-screen flash overlay, auto-dismissed
  const flashTimer = useRef(null);

  useEffect(() => { primeAudio(); }, []);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  // Trigger a full-screen colour flash. Longer for wrong scans (loud alarm),
  // short for OK/dup so scanning stays fast.
  const triggerFlash = useCallback((status) => {
    if (flashTimer.current) clearTimeout(flashTimer.current);
    setFlash({ status });
    flashTimer.current = setTimeout(() => setFlash(null), status === "wrong" ? 900 : 380);
  }, []);

  useEffect(() => {
    axios.get(`${API}/delivery-routes?include_progress=true`, { headers: { "x-no-cache": "1" } })
      .then((res) => {
        // Only planned/pending runs qualify for truck loading — completed
        // runs are done, and once the first stop is delivered the truck
        // has already left the depot so loading no longer applies.
        const active = (res.data || [])
          .filter((r) => (r.status === "planned" || r.status === "pending") && (r.progress_done ?? 0) === 0);
        active.sort((a, b) => (a.delivery_date || "").localeCompare(b.delivery_date || ""));
        setRoutes(active);
      })
      .catch(() => toast.error("Couldn't load runs"))
      .finally(() => setRoutesLoading(false));
  }, []);

  const pickRoute = async (r) => {
    setSelected(r);
    setManifest(null);
    setLoaded(new Set());
    setLastScan(null);
    try {
      const res = await axios.get(`${API}/delivery-routes/${r.id}/loading-manifest`, { headers: { "x-no-cache": "1" } });
      setManifest(res.data);
    } catch (e) {
      toast.error("Couldn't load bag manifest");
      setSelected(null);
    }
  };

  const bagByBarcode = useMemo(() => {
    const m = new Map();
    for (const b of (manifest?.bags || [])) m.set((b.barcode || "").toUpperCase(), b);
    return m;
  }, [manifest]);

  const onScan = useCallback((raw) => {
    const code = String(raw || "").trim().toUpperCase();
    if (!code) return;
    const bag = bagByBarcode.get(code);
    if (!bag) {
      beep.error();
      try { navigator.vibrate?.([120, 60, 120, 60, 120]); } catch { /* ignored */ }
      setLastScan({ status: "wrong", code });
      triggerFlash("wrong");
      return;
    }
    setLoaded((prev) => {
      if (prev.has(code)) {
        beep.tick();
        setLastScan({ status: "dup", bag, code });
        triggerFlash("dup");
        return prev;
      }
      beep.ok();
      try { navigator.vibrate?.(40); } catch { /* ignored */ }
      setLastScan({ status: "ok", bag, code });
      triggerFlash("ok");
      const next = new Set(prev);
      next.add(code);
      return next;
    });
  }, [bagByBarcode, triggerFlash]);

  const totalBags = manifest?.total_bags || 0;
  const allLoaded = totalBags > 0 && loaded.size >= totalBags;

  // Remaining bags grouped by hotel, in stop order.
  const remainingByHotel = useMemo(() => {
    const groups = new Map();
    for (const b of (manifest?.bags || [])) {
      if (loaded.has((b.barcode || "").toUpperCase())) continue;
      const k = `${b.stop_no}. ${b.hotel_name}`;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(b);
    }
    return Array.from(groups.entries());
  }, [manifest, loaded]);

  return (
    <PdaLayout
      title="Truck Loading"
      subtitle={selected ? (selected.name || "Run") : "Verify bags before departure"}
      onBack={() => (selected ? (setSelected(null), setManifest(null)) : navigate("/pda/menu"))}
      onLogout={onLogout}
    >
      {!selected ? (
        <>
          <div className="rounded-md border bg-white px-3 py-2 mb-3 text-xs text-neutral-600 flex items-center gap-2">
            <Boxes className="h-4 w-4 text-orange-600" />
            Optional check — pick your run, then scan each bag&apos;s barcode as you load. Screen flashes green for OK, red alarm for wrong-run bags.
          </div>
          {routesLoading ? (
            <div className="px-3 py-4 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading runs…</div>
          ) : routes.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-neutral-500">No planned runs.</div>
          ) : (
            <div className="space-y-2">
              {routes.map((r) => (
                <button
                  key={r.id}
                  onClick={() => pickRoute(r)}
                  data-testid={`pda-loading-route-${r.id}`}
                  className="w-full text-left rounded-md border bg-white px-3 py-3 active:bg-orange-50"
                >
                  <div className="font-display font-bold text-base truncate">{r.name || "Run"}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {r.delivery_date || "No date"}
                    {r.driver ? ` · ${r.driver}` : ""}{r.vehicle ? ` · ${r.vehicle}` : ""}
                    {" · "}{(r.stop_sequence || []).filter((s) => s.kind === "order").length} stops
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      ) : !manifest ? (
        <div className="px-3 py-6 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading bag manifest…</div>
      ) : (
        <>
          {/* Current run summary — big & bold so the driver never wonders
              which truck they're loading. */}
          <div className="rounded-md border-2 border-brand-navy bg-brand-navy text-white px-3 py-2 mb-3" data-testid="pda-loading-run-header">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold text-base truncate">
                  {manifest.route?.name || "Run"}
                </div>
                <div className="text-[11px] text-white/80 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {manifest.route?.driver ? (
                    <span className="flex items-center gap-1"><User className="h-3 w-3" />{manifest.route.driver}</span>
                  ) : <span className="italic opacity-60">No driver</span>}
                  {manifest.route?.vehicle ? (
                    <span className="flex items-center gap-1"><Car className="h-3 w-3" />{manifest.route.vehicle}</span>
                  ) : null}
                  {manifest.route?.delivery_date ? (
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{manifest.route.delivery_date}</span>
                  ) : null}
                </div>
              </div>
              <button
                onClick={() => { setManifest(null); setLoaded(new Set()); setLastScan(null); }}
                data-testid="pda-loading-change-run"
                className="text-[11px] px-2 py-1 rounded border border-white/40 hover:bg-white/10 shrink-0"
              >
                Change run
              </button>
            </div>
          </div>

          <BarcodeCapture onScan={onScan} />

          {/* Big verify feedback */}
          {lastScan && (
            <div
              data-testid="pda-loading-feedback"
              className={`rounded-md px-3 py-4 mb-3 text-center border-2 ${
                lastScan.status === "wrong"
                  ? "bg-rose-600 border-rose-700 text-white"
                  : lastScan.status === "dup"
                    ? "bg-amber-100 border-amber-300 text-amber-900"
                    : "bg-emerald-600 border-emerald-700 text-white"
              }`}
            >
              {lastScan.status === "wrong" ? (
                <>
                  <XCircle className="h-10 w-10 mx-auto mb-1" />
                  <div className="font-display font-bold text-2xl">NOT ON THIS RUN</div>
                  <div className="font-mono text-xs mt-1 opacity-80">{lastScan.code}</div>
                </>
              ) : (
                <>
                  <CheckCircle className="h-10 w-10 mx-auto mb-1" />
                  <div className="font-display font-bold text-2xl">
                    {lastScan.status === "dup" ? "Already scanned" : "OK — load it"}
                  </div>
                  <div className="text-sm mt-1">
                    Bag #{lastScan.bag.bundle_no} · {lastScan.bag.hotel_name} ({lastScan.bag.pcs} pcs)
                  </div>
                </>
              )}
            </div>
          )}

          {/* Progress */}
          <div className={`rounded-md border px-3 py-3 mb-3 text-center ${allLoaded ? "bg-emerald-50 border-emerald-300" : "bg-white"}`}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Loaded</div>
            <div className="font-display font-bold text-4xl" data-testid="pda-loading-count">
              {loaded.size}<span className="text-neutral-400">/{totalBags}</span>
            </div>
            {allLoaded && <div className="text-emerald-700 font-bold text-sm mt-1">✓ All bags on board</div>}
            {loaded.size > 0 && (
              <button
                onClick={() => { setLoaded(new Set()); setLastScan(null); }}
                data-testid="pda-loading-reset"
                className="mt-2 text-xs text-rose-700 underline flex items-center gap-1 mx-auto"
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
            )}
          </div>

          {/* Remaining bags, grouped by stop */}
          {remainingByHotel.length > 0 && (
            <div className="rounded-md border bg-white" data-testid="pda-loading-remaining">
              <div className="px-3 py-2 border-b text-xs uppercase tracking-wider text-neutral-600">
                Still to load · {totalBags - loaded.size}
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {remainingByHotel.map(([hotel, bags]) => (
                  <div key={hotel} className="px-3 py-2">
                    <div className="text-xs font-bold text-neutral-700">{hotel}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {bags.map((b) => `Bag #${b.bundle_no} (${b.pcs} pcs)`).join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Full-screen colour flash — instant confirmation for the driver even
          when they're not looking at the small screen. Auto-dismisses. */}
      {flash && (
        <div
          data-testid={`pda-loading-flash-${flash.status}`}
          className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center pointer-events-none ${
            flash.status === "wrong"
              ? "bg-rose-600 animate-pulse"
              : flash.status === "dup"
                ? "bg-amber-400/95"
                : "bg-emerald-500"
          }`}
        >
          {flash.status === "wrong" ? (
            <>
              <XCircle className="h-32 w-32 text-white mb-4" strokeWidth={2.5} />
              <div className="font-display font-black text-white text-5xl tracking-wide">STOP</div>
              <div className="font-display font-bold text-white/90 text-2xl mt-2">Wrong run</div>
            </>
          ) : flash.status === "dup" ? (
            <>
              <CheckCircle className="h-32 w-32 text-amber-900 mb-4" strokeWidth={2.5} />
              <div className="font-display font-black text-amber-900 text-4xl">Already loaded</div>
            </>
          ) : (
            <>
              <CheckCircle className="h-32 w-32 text-white mb-4" strokeWidth={2.5} />
              <div className="font-display font-black text-white text-5xl">OK</div>
            </>
          )}
        </div>
      )}
    </PdaLayout>
  );
}

// ── Barcode capture input — same battle-tested IME pattern as the Dispatch
//    bag step (Enter, compositionend, and a 50ms stability-polling fallback).
function BarcodeCapture({ onScan }) {
  const inputRef = useRef(null);
  const inFlight = useRef(false);

  const doSubmit = useCallback((raw) => {
    if (inFlight.current) return;
    const code = String(raw || "").trim().replace(/[\r\n\t]+/g, "");
    if (!code || code.length < 3) return;
    inFlight.current = true;
    if (inputRef.current) inputRef.current.value = "";
    try { onScan?.(code); } finally {
      setTimeout(() => { inFlight.current = false; inputRef.current?.focus(); }, 60);
    }
  }, [onScan]);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let lastVal = "";
    let stableSince = Date.now();
    const id = setInterval(() => {
      const el = inputRef.current;
      if (!el || inFlight.current) return;
      const cur = el.value;
      if (cur !== lastVal) { lastVal = cur; stableSince = Date.now(); return; }
      if (cur.length >= 3 && Date.now() - stableSince >= 150) {
        doSubmit(cur);
        lastVal = "";
        stableSince = Date.now();
      }
    }, 50);
    return () => clearInterval(id);
  }, [doSubmit]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const onCompEnd = () => doSubmit(el.value);
    const onCompUpdate = (e) => { if (e.data) el.value = e.data; };
    el.addEventListener("compositionend", onCompEnd);
    el.addEventListener("compositionupdate", onCompUpdate);
    return () => {
      el.removeEventListener("compositionend", onCompEnd);
      el.removeEventListener("compositionupdate", onCompUpdate);
    };
  }, [doSubmit]);

  return (
    <div
      className="rounded-md border-2 border-orange-600 bg-white px-3 py-2 mb-3 flex items-center gap-2"
      onClick={() => setTimeout(() => inputRef.current?.focus(), 50)}
    >
      <ScanLine className="h-5 w-5 text-orange-600 shrink-0" />
      <input
        ref={inputRef}
        type="text"
        defaultValue=""
        placeholder="Scan bag barcode…"
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); doSubmit(e.currentTarget.value); }
        }}
        onBlur={() => setTimeout(() => inputRef.current?.focus(), 50)}
        autoFocus
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="flex-1 bg-transparent outline-none text-base font-mono placeholder:text-neutral-400"
        data-testid="pda-loading-capture-input"
        aria-label="Bag barcode capture"
      />
    </div>
  );
}
